import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ApiError, api, type UpdateProblemCardBody } from '../lib/api'
import { addressPayloadOf, formValueOf, toEditPayload, type ProblemFormValue } from '../lib/problemForm'
import { ProblemCardForm } from '../components/ProblemCardForm'

/**
 * 시민 수정 — **등록 폼을 그대로 열되 등록 당시 값이 채워져 있다**(09-04 합의).
 *
 * 본인 확인은 등록 201 응답의 `editPath`에 실린 **토큰 하나**다(계정이 없다). 토큰이 없거나
 * 틀리면 서버가 404로 답하고 — 카드 부재와 구분하지 않는다 — 화면도 같은 문구를 낸다.
 *
 * **저장은 부분 갱신이다**(09-04) — 안 보낸 축은 서버가 지키고, 지우는 것은 `null` 명시다.
 * 그래도 조회가 끝나기 전에는 폼을 그리지 않는다: 빈 폼을 열면 시민이 손대지 않은 칸이
 * 「비웠다」로 보내져 실제로 지워진다.
 *
 * **고치면 다시 검수를 받는다** — 다이렉트 모드가 켜져 있어도 수정본은 자동 공개되지 않는다
 * (08-31 확정). 그 사실을 저장 뒤 화면이 그대로 말한다.
 */
export function ProblemEditPage() {
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  const token = params.get('t') ?? ''
  const queryClient = useQueryClient()

  const card = useQuery({
    queryKey: ['problem-card-edit', id, token],
    queryFn: () => api.problemCardEdit(id, token),
    enabled: id !== '' && token !== '',
    // 토큰이 틀린 404를 재시도해 봐야 같은 답이다.
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: (body: UpdateProblemCardBody) => api.updateProblemCard(id, token, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['problem-cards'] })
      void queryClient.invalidateQueries({ queryKey: ['problem-card-edit', id, token] })
    },
  })

  const handleSubmit = (v: ProblemFormValue) => {
    mutation.mutate({
      // 「비운 칸」을 지움으로 명시한다 — 부분 갱신이라 키를 빼면 종전 값이 남는다.
      ...toEditPayload(v),
      // 수정은 평면 `regionCode`도 함께 받는다(등록과 달리 필수) — 주소 객체는 원본 보존용이다.
      regionCode: v.address.regionCode,
      address: addressPayloadOf(v.address),
      // **폼에 칸이 없는 축**(`targetEtcText`·`pilotConditionNote`)**은 아예 안 보낸다.**
      // 서버가 종전 값을 지키므로 되실을 필요가 없고, 되실으면 오히려 「기타」를 뺀 수정이
      // 400이 된다(주 대상에서 「기타」가 빠지면 기타 사유는 남을 자리가 없다).
    })
  }

  if (token === '') {
    return (
      <NotAllowed message="수정 링크가 올바르지 않습니다. 등록을 마쳤을 때 받은 링크 전체를 다시 열어 주세요." />
    )
  }

  if (card.isPending) {
    return (
      <div className="panel">
        <p className="hint" role="status">
          등록하신 내용을 불러오는 중입니다.
        </p>
      </div>
    )
  }

  if (card.isError) {
    const status = card.error instanceof ApiError ? card.error.status : 0
    return (
      <NotAllowed
        message={
          status === 404
            ? '이 링크로는 열 수 없습니다. 링크가 만료됐거나 주소가 잘린 것일 수 있습니다.'
            : '지금은 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
        }
      />
    )
  }

  if (mutation.isSuccess) {
    return (
      <div className="panel">
        <h1 className="page-title">수정한 내용을 저장했습니다</h1>
        <p className="page-desc">
          고치신 내용은 운영자 확인을 거친 뒤 다시 공개됩니다. 확인 전까지는 공개 목록에 보이지
          않을 수 있습니다.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn" to="/problems">
            시민문제카드 목록으로
          </Link>
          <button className="btn ghost" type="button" onClick={() => mutation.reset()}>
            더 고치기
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Link className="back-link" to="/problems">
        ← 시민문제카드 목록
      </Link>
      <h1 className="page-title">등록한 문제 고치기</h1>
      <p className="page-desc">
        등록하실 때 적어 주신 내용이 그대로 채워져 있습니다. 고칠 곳만 바꾸고 저장해 주세요.
      </p>

      <div className="notice" style={{ marginBottom: 24 }}>
        저장하면 <b>운영자 확인을 다시 거칩니다.</b> 비운 칸은 지워지니, 남기고 싶은 내용은 그대로
        두세요.
      </div>

      <ProblemCardForm
        // 수정만 넓은 축을 그린다 — FE에 대응 화면이 없어 좁힐 기준이 없고, 서버
        // `GET/PUT …/edit`이 등록 당시 값 전부를 주고받는다(`ProblemCardForm` 머리말).
        mode="edit"
        // 조회가 끝난 뒤에만 마운트된다 — 초기값은 이 시점에 확정이다.
        initial={formValueOf(card.data)}
        submitLabel="수정 저장하기"
        pendingLabel="저장하는 중…"
        isPending={mutation.isPending}
        error={mutation.error}
        onSubmit={handleSubmit}
      />
    </>
  )
}

/** 열 수 없는 링크. **왜 안 되는지는 갈라 말하지 않는다** — 서버가 404 하나로 답하는 이유와 같다. */
function NotAllowed({ message }: { message: string }) {
  return (
    <div className="panel">
      <h1 className="page-title">수정 화면을 열 수 없습니다</h1>
      <p className="page-desc">{message}</p>
      <Link className="btn" to="/problems">
        시민문제카드 목록으로
      </Link>
    </div>
  )
}
